import React, { useEffect, useState } from 'react';
import { Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Autocomplete, Paper, Dialog, DialogActions, DialogContent, DialogTitle, Button, LinearProgress } from '@mui/material';
import ButtonOutlined from "../../StyledComponents/ButtonOutlined";
import ButtonContained from "../../StyledComponents/ButtonContained";
import httpRequest from "../../../httpRequest";
import { Delete, Add } from '@mui/icons-material';

const InvoiceDataTable = ({ data, vendorCode, rawVendorData, onDataChange, previewMode }) => {
  const [parsedData, setParsedData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCell, setEditingCell] = useState({ rowIndex: null, field: '', value: '' });
  const [supplierInfo, setSupplierInfo] = useState({});
  const [currentVendorCode, setCurrentVendorCode] = useState(vendorCode);
  const [vendorOptions, setVendorOptions] = useState([]);

  useEffect(() => {
    setCurrentVendorCode(vendorCode);
  }, [vendorCode]);

  useEffect(() => {
    fetchVendorInfo();
    try {
      const parsedData = JSON.parse(data);
      if (!parsedData.Items || parsedData.Items.length === 0) {
        parsedData.Items = [{}]; // Ensure at least one item row
      }
      setParsedData(parsedData);
    } catch (error) {
      console.error('Failed to parse data:', error);
    }
  }, [data, currentVendorCode]);

  const fetchVendorInfo = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-vendors`);
      const vendors = response.data.vendors;

      const options = vendors.map(vendor => ({
        label: vendor.vendor_name,
        value: vendor.vendor_code,
        search: `${vendor.vendor_name} ${vendor.vendor_code}`
      }));

      setVendorOptions(options);

      const foundVendor = vendors.find(vendor => vendor.vendor_code === currentVendorCode);

      if (foundVendor) {
        setSupplierInfo(foundVendor);
      } else if (rawVendorData) {
        console.log("Raw Vendor Data:", rawVendorData);

        let parsedRawVendorData;
        try {
          parsedRawVendorData = JSON.parse(rawVendorData);
          console.log("Parsed Raw Vendor Data:", parsedRawVendorData);
        } catch (parseError) {
          console.error("Error parsing rawVendorData:", parseError);
          return;
        }

        const {
          "Vendor Name": vendor_name = 'N/A',
          "Vendor Address": vendor_address = 'N/A',
          "Vendor Email": vendor_email = 'N/A',
          "Vendor Contact": vendor_contact = 'N/A',
          "Bank Name": bank_name = 'N/A',
          "Account Number": account_number = 'N/A',
          "IBAN Number": iban_no = 'N/A'
        } = parsedRawVendorData;

        setSupplierInfo({
          vendor_name,
          vendor_address,
          vendor_email,
          vendor_contact,
          bank_name,
          account_number,
          iban_no
        });
      }
    } catch (error) {
      console.error("Error fetching vendor information:", error);
    }
  };

  const handleVendorCodeChange = (event, newValue) => {
    const updatedVendorCode = newValue ? newValue.value : '';
    setCurrentVendorCode(updatedVendorCode);
    onDataChange(parsedData, updatedVendorCode);
  };

  const handleTextFieldChange = (field, value) => {
    const updatedData = { ...parsedData, [field]: value };
    setParsedData(updatedData);
    onDataChange(updatedData, currentVendorCode);
  };

  const handleItemFieldChange = (index, itemField, event) => {
    const updatedItems = parsedData.Items.map((item, i) =>
      i === index ? { ...item, [itemField]: event.target.value } : item
    );
    const updatedData = { ...parsedData, Items: updatedItems };
    setParsedData(updatedData);
    onDataChange(updatedData, currentVendorCode);
  };

  const handleAddRow = () => {
    const newItem = Object.keys(parsedData.Items[0]).reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, {});
    const updatedItems = [...parsedData.Items, newItem];
    const updatedData = { ...parsedData, Items: updatedItems };
    setParsedData(updatedData);
    onDataChange(updatedData);
  };

  const handleDeleteRow = (index) => {
    const updatedItems = parsedData.Items.filter((_, i) => i !== index);
    if (updatedItems.length === 0) {
      updatedItems.push({});
    }
    const updatedData = { ...parsedData, Items: updatedItems };
    setParsedData(updatedData);
    onDataChange(updatedData);
  };

  const stripSpecialCharacters = (value) => {
    // Handle null, undefined, or empty string
    if (value == null || value === '') {
      return 0;
    }
    // Convert to string and remove non-numeric characters
    const cleaned = String(value).replace(/[^0-9.-]+/g, '');
    return parseFloat(cleaned) || 0;
  };

  const calculateTotal = (field) => {
    if (!parsedData?.Items) {
      return "0.00";
    }
    const total = parsedData.Items.reduce((sum, item) => {
      return sum + stripSpecialCharacters(item[field]);
    }, 0);
    return total.toFixed(2); // Format to two decimal places
  };

  const handleCellClick = (rowIndex, field, value) => {
    setEditingCell({ rowIndex, field, value: value || '' });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogSave = () => {
    const updatedItems = parsedData.Items.map((item, i) =>
      i === editingCell.rowIndex ? { ...item, [editingCell.field]: editingCell.value } : item
    );
    const updatedData = { ...parsedData, Items: updatedItems };
    setParsedData(updatedData);
    onDataChange(updatedData);
    setDialogOpen(false);
  };

  const handleDialogChange = (event) => {
    setEditingCell({ ...editingCell, value: event.target.value });
  };

  if (!parsedData) {
    return <LinearProgress />;
  }

  return (
    <Grid container spacing={2}>
      {/* Invoice Data */}
      <Grid item xs={12} md={6}>
        <Paper elevation={3} sx={{ p: 2, borderRadius: 1, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.5)', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <TableContainer>
            <Table size="small" aria-label="invoice-data-table">
              <TableBody>
                {Object.keys(parsedData).map((field) => (
                  field !== 'Items' && field !== 'Vendor Name' && (
                    <TableRow key={field}>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          fontFamily: "SapFiori72, sans-serif",
                          backgroundColor: '#354a5f',
                          color: '#fff',
                          width: '190px'
                        }}
                      >
                        {field}
                      </TableCell>
                      <TableCell sx={{ width: 'auto' }}>
                        <TextField
                          value={parsedData[field] || ''}
                          onChange={(event) => {
                            handleTextFieldChange(field, event.target.value);
                          }}
                          variant="standard"
                          size="small"
                          fullWidth
                          disabled={previewMode}
                          margin="none"
                          inputProps={{
                            style: {
                              textAlign: field === 'Total Amount' || field === 'Net Amount' ? 'right' : 'left'
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  )
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      {/* Supplier Information */}
      <Grid item xs={12} md={6}>
        <Paper elevation={3} sx={{ p: 2, borderRadius: 1, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.5)', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <TableContainer>
            <Table size="small" aria-label="supplier-info-table">
              <TableBody>
                <TableRow sx={{ '& td': { fontFamily: "SapFiori72, sans-serif", width: 'auto' } }}>
                  <TableCell align="left" sx={{ backgroundColor: '#354a5f', p: 1.85, color: '#fff' }}>Vendor Code</TableCell>
                  <TableCell align="left" sx={{ minWidth: '300px' }}>
                    <Autocomplete
                      disabled={previewMode}
                      options={vendorOptions}
                      getOptionLabel={(option) => option.value}
                      filterOptions={(options, state) =>
                        options.filter((option) =>
                          option.search.toLowerCase().includes(state.inputValue.toLowerCase())
                        )
                      }
                      renderOption={(props, option) => (
                        <li {...props} key={option.value}>
                          {option.label}
                        </li>
                      )}
                      value={vendorOptions.find((option) => option.value === currentVendorCode) || null}
                      onChange={handleVendorCodeChange}
                      isOptionEqualToValue={(option, value) => option.value === value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!currentVendorCode}
                          helperText={!currentVendorCode ? 'Please select a vendor' : ''}
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
                <TableRow sx={{ '& td': { fontFamily: "SapFiori72, sans-serif" } }}>
                  <TableCell align="left" sx={{ backgroundColor: '#354a5f', p: 1.6, color: '#fff' }}>Vendor Name</TableCell>
                  <TableCell align="left">{supplierInfo && supplierInfo.vendor_name ? supplierInfo.vendor_name : 'N/A'}</TableCell>
                </TableRow>
                <TableRow sx={{ '& td': { fontFamily: "SapFiori72, sans-serif" } }}>
                  <TableCell align="left" sx={{ backgroundColor: '#354a5f', p: 1.6, color: '#fff' }}>Vendor Address</TableCell>
                  <TableCell align="left">{supplierInfo && supplierInfo.vendor_address ? supplierInfo.vendor_address : 'N/A'}</TableCell>
                </TableRow>
                <TableRow sx={{ '& td': { fontFamily: "SapFiori72, sans-serif" } }}>
                  <TableCell align="left" sx={{ backgroundColor: '#354a5f', p: 1.6, color: '#fff' }}>Vendor Email</TableCell>
                  <TableCell align="left">{supplierInfo && supplierInfo.vendor_email ? supplierInfo.vendor_email : 'N/A'}</TableCell>
                </TableRow>
                <TableRow sx={{ '& td': { fontFamily: "SapFiori72, sans-serif" } }}>
                  <TableCell align="left" sx={{ backgroundColor: '#354a5f', p: 1.6, color: '#fff' }}>Vendor Contact</TableCell>
                  <TableCell align="left">{supplierInfo && supplierInfo.vendor_contact ? supplierInfo.vendor_contact : 'N/A'}</TableCell>
                </TableRow>
                <TableRow sx={{ '& td': { fontFamily: "SapFiori72, sans-serif" } }}>
                  <TableCell align="left" sx={{ backgroundColor: '#354a5f', p: 1.6, color: '#fff' }}>Bank Name</TableCell>
                  <TableCell align="left">{supplierInfo && supplierInfo.bank_name ? supplierInfo.bank_name : 'N/A'}</TableCell>
                </TableRow>
                <TableRow sx={{ '& td': { fontFamily: "SapFiori72, sans-serif" } }}>
                  <TableCell align="left" sx={{ backgroundColor: '#354a5f', p: 1.6, color: '#fff' }}>Account Number</TableCell>
                  <TableCell align="left">{supplierInfo && supplierInfo.account_number ? supplierInfo.account_number : 'N/A'}</TableCell>
                </TableRow>
                <TableRow sx={{ '& td': { fontFamily: "SapFiori72, sans-serif" } }}>
                  <TableCell align="left" sx={{ backgroundColor: '#354a5f', p: 1.7, color: '#fff' }}>IBAN Number</TableCell>
                  <TableCell align="left">{supplierInfo && supplierInfo.iban_no ? supplierInfo.iban_no : 'N/A'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      {/* Items Table */}
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 2, borderRadius: 1, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.5)', marginTop: '16px' }}>
          <TableContainer>
            <Table size="small" aria-label="items-table">
              <TableHead>
                <TableRow sx={{ '& th': { border: '1px solid #ccc' } }}>
                  {Object.keys(parsedData.Items[0]).map((itemField) => {
                    let label = itemField;
                    switch (itemField) {
                      case 'Item Number':
                        label = 'Item #';
                        break;
                      case 'Quantity':
                        label = 'Qty';
                        break;
                      case 'Unit of Measure':
                        label = 'UoM';
                        break;
                      default:
                        label = itemField;
                    }
                    return (
                      <TableCell
                        key={itemField}
                        sx={{
                          fontFamily: "SapFiori72, sans-serif",
                          backgroundColor: '#354a5f',
                          color: '#fff',
                          minWidth: itemField === 'Material Description' ? '300px' : '80px',
                          textAlign:
                            itemField === 'Material Description' ? 'left' :
                            (itemField === 'Price' || itemField === 'Total Amount' || itemField === 'Tax Amount' || itemField === 'Net Amount') ? 'right' : 'center',
                        }}
                      >
                        {label}
                      </TableCell>
                    );
                  })}
                  {!previewMode && (
                    <TableCell sx={{ backgroundColor: '#354a5f' }}>
                      <ButtonOutlined onClick={handleAddRow} size="small">
                        <Add />
                      </ButtonOutlined>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedData.Items.map((item, itemIndex) => (
                  <TableRow key={itemIndex}>
                    {Object.keys(item).map((itemField) => (
                      <TableCell
                        key={itemField}
                        sx={{
                          minWidth: itemField === 'Material Description' ? '200px' : '80px',
                          textAlign: itemField === 'Price' || itemField === 'Total Amount' || itemField === 'Tax Amount' || itemField === 'Net Amount' ? 'right' : 'center',
                        }}
                        onClick={() => {
                          if (previewMode) return;
                          if (
                            itemField !== 'Item Number' &&
                            itemField !== 'Quantity' &&
                            itemField !== 'Unit of Measure' &&
                            itemField !== 'Price' &&
                            itemField !== 'Total Amount' &&
                            itemField !== 'Tax Amount' &&
                            itemField !== 'Net Amount'
                          ) {
                            handleCellClick(itemIndex, itemField, item[itemField]);
                          }
                        }}
                      >
                        <TextField
                          value={item[itemField] || ''}
                          onChange={(event) => {
                            const newValue = event.target.value;
                            handleItemFieldChange(itemIndex, itemField, { target: { value: newValue } });
                          }}
                          variant="standard"
                          size="small"
                          fullWidth
                          disabled={previewMode}
                          margin="none"
                          inputProps={{
                            style: {
                              textAlign: itemField === 'Material Description' ? 'left' :
                                (itemField === 'Price' || itemField === 'Total Amount' || itemField === 'Tax Amount' || itemField === 'Net Amount') ? 'right' : 'center'
                            }
                          }}
                        />
                      </TableCell>
                    ))}
                    {!previewMode && (
                      <TableCell>
                        <ButtonContained onClick={() => handleDeleteRow(itemIndex)} size="small">
                          <Delete />
                        </ButtonContained>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                <TableRow>
                  {Object.keys(parsedData.Items[0]).map((itemField) => (
                    <TableCell
                      key={itemField}
                      sx={{
                        color: '#fff',
                        p: 2,
                        backgroundColor: '#354a5f',
                        minWidth: itemField === 'Material Description' ? '200px' : '80px',
                        textAlign: itemField === 'Price' || itemField === 'Total Amount' || itemField === 'Tax Amount' || itemField === 'Net Amount' ? 'right' : 'center',
                      }}
                    >
                      {itemField === 'Total Amount' ? calculateTotal('Total Amount') :
                        itemField === 'Tax Amount' ? calculateTotal('Tax Amount') :
                        itemField === 'Net Amount' ? calculateTotal('Net Amount') : ''}
                    </TableCell>
                  ))}
                  {!previewMode && (
                    <TableCell sx={{ p: 2, backgroundColor: '#354a5f' }}></TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      {/* Dialog for Editing Cell */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Edit {editingCell.field}</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            value={editingCell.value}
            onChange={handleDialogChange}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDialogSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default InvoiceDataTable;